/**
 * authedDownload
 *
 * Trigger an authenticated file download via a blob URL, sending both the
 * SameSite cookie (`credentials: 'include'`) and an Authorization header
 * when a bearer token is available. Using a blob URL (instead of a plain
 * `<a href>` navigation) ensures downloads work in strict-privacy browsers
 * where the cookie may not travel on a plain link.
 *
 * Usage:
 *   import { authedDownload } from '../lib/authedDownload';
 *   await authedDownload('/investor-docs/download?path=...', 'file.md', token);
 */
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export async function authedDownload(endpoint, suggestedFilename, token) {
  try {
    const res = await fetch(`${API}${endpoint}`, {
      method: 'GET',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        toast.error('Session expired or not authorized. Please log in again and retry.');
      } else {
        toast.error(`Download failed: HTTP ${res.status}`);
      }
      return false;
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = suggestedFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Free the blob URL after a short delay so the download has time to start
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return true;
  } catch (err) {
    toast.error(`Download failed: ${err.message || 'network error'}`);
    return false;
  }
}

export default authedDownload;
